/**
 * BlockRenderer - Main component that renders structured content blocks
 * Replaces regex-heavy FormattedAIResponse with a clean block-based architecture
 */

import React, { useState, useMemo } from 'react';
import type { ContentBlock } from '@/types/contentBlocks';
import { parseContentSync } from '@/utils/contentParser';
import { useStreamingEffect } from '@/hooks/useStreamingEffect';
import { LinkConfirmationModal } from './LinkConfirmationModal';

// Import all block renderers
import { ParagraphBlockRenderer } from './blocks/ParagraphBlock';
import { HeadingBlockRenderer } from './blocks/HeadingBlock';
import { CodeBlockRenderer } from './blocks/CodeBlockRenderer';
import { MathBlockRenderer } from './blocks/MathBlock';
import { ChemistryBlockRenderer } from './blocks/ChemistryBlock';
import { TableBlockRenderer } from './blocks/TableBlock';
import { ListBlockRenderer } from './blocks/ListBlock';
import { LinkBlockRenderer } from './blocks/LinkBlock';
import { ImageBlockRenderer } from './blocks/ImageBlock';
import { MemeBlockRenderer } from './blocks/MemeBlock';
import { BlockquoteBlockRenderer } from './blocks/BlockquoteBlock';
import { HtmlBlockRenderer } from './blocks/HtmlBlock';
import { HrBlockRenderer } from './blocks/HrBlock';
import { ChartBlockRenderer } from './blocks/ChartBlockRenderer';
import { FileCreationBlockRenderer } from './blocks/FileCreationBlock';
import { CitationSource } from '@/utils/citationReplacer';

interface BlockRendererProps {
  content: string;
  isStreaming?: boolean;
  onComplete?: () => void;
  isDarkMode?: boolean;
  className?: string;
  modelName?: string;
  agentName?: string;
  citationSources?: CitationSource[];
}

export const BlockRenderer: React.FC<BlockRendererProps> = ({
  content,
  isStreaming = false,
  onComplete,
  isDarkMode = false,
  className = '',
  modelName,
  agentName,
  citationSources,
}) => {
  // Handle streaming completion
  useStreamingEffect({ isStreaming, onComplete });

  // State for link confirmation modal
  const [linkModal, setLinkModal] = useState<{
    isOpen: boolean;
    url: string;
  }>({
    isOpen: false,
    url: '',
  });

  // Parse content into blocks
  const parsedContent = useMemo(() => {
    try {
      return parseContentSync(content);
    } catch (error) {
      console.error('Content parsing error:', error);
      // Fallback to simple paragraph
      return {
        blocks: [
          {
            type: 'paragraph' as const,
            id: 'fallback-0',
            content: content,
          },
        ],
        metadata: {
          hasCode: false,
          hasMath: false,
          hasChemistry: false,
          hasTables: false,
        },
      };
    }
  }, [content]);

  // Handle link clicks
  const handleLinkClick = (url: string) => {
    setLinkModal({
      isOpen: true,
      url,
    });
  };

  const handleModalClose = () => {
    setLinkModal({
      isOpen: false,
      url: '',
    });
  };

  const handleModalConfirm = () => {
    if (linkModal.url) {
      window.open(linkModal.url, '_blank', 'noopener,noreferrer');
    }
    handleModalClose();
  };

  // Render individual block
  const renderBlock = (block: ContentBlock) => {
    const key = block.id;

    switch (block.type) {
      case 'paragraph':
        return (
          <ParagraphBlockRenderer
            key={key}
            block={block}
            isDarkMode={isDarkMode}
            onLinkClick={handleLinkClick}
            citationSources={citationSources}
          />
        );

      case 'heading':
        return <HeadingBlockRenderer key={key} block={block} isDarkMode={isDarkMode} />;

      case 'code':
        return <CodeBlockRenderer key={key} block={block} isDarkMode={isDarkMode} isStreaming={isStreaming} />;

      case 'math':
        return <MathBlockRenderer key={key} block={block} isDarkMode={isDarkMode} />;

      case 'chemistry':
        return <ChemistryBlockRenderer key={key} block={block} isDarkMode={isDarkMode} />;

      case 'table':
        return <TableBlockRenderer key={key} block={block} isDarkMode={isDarkMode} />;

      case 'list':
        return <ListBlockRenderer key={key} block={block} isDarkMode={isDarkMode} citationSources={citationSources} />;

      case 'link':
        return (
          <LinkBlockRenderer
            key={key}
            block={block}
            isDarkMode={isDarkMode}
            onLinkClick={handleLinkClick}
          />
        );

      case 'image':
        return <ImageBlockRenderer key={key} block={block} isDarkMode={isDarkMode} />;

      case 'meme':
        return <MemeBlockRenderer key={key} block={block} isDarkMode={isDarkMode} />;

      case 'blockquote':
        return <BlockquoteBlockRenderer key={key} block={block} isDarkMode={isDarkMode} />;

      case 'html':
        return <HtmlBlockRenderer key={key} block={block} isDarkMode={isDarkMode} />;

      case 'hr':
        return <HrBlockRenderer key={key} block={block} isDarkMode={isDarkMode} />;

      case 'chart':
        return <ChartBlockRenderer key={key} block={block} isDarkMode={isDarkMode} />;

      case 'file-creation':
        return (
          <FileCreationBlockRenderer
            key={key}
            block={block as any}
            isDarkMode={isDarkMode}
          />
        );

      default:
        console.warn('Unknown block type:', (block as any).type);
        return null;
    }
  };

  return (
    <>
      <div className={`prose max-w-none ${isDarkMode ? 'prose-invert' : ''} ${className}`}>
        {/* KaTeX styling */}
        <style dangerouslySetInnerHTML={{
          __html: `
            .katex { 
              font-size: 1.1em !important;
              font-family: 'KaTeX_Main', 'Times New Roman', serif !important;
            }
            .katex-display { 
              margin: 1em 0; 
              overflow-x: auto; 
              overflow-y: hidden; 
              text-align: left;
            }
            .katex-display > .katex { 
              display: inline-block; 
              white-space: nowrap; 
            }
            .katex .base { 
              margin: 0.25em 0; 
            }
            .katex .mrel { 
              padding: 0 0.2em; 
            }
            .chemical-equation .katex .mrel {
              padding: 0 0.5em;
            }
          `
        }} />

        {/* Render all blocks */}
        {parsedContent.blocks.map(renderBlock)}
      </div>

      {/* Link Confirmation Modal */}
      <LinkConfirmationModal
        isOpen={linkModal.isOpen}
        onClose={handleModalClose}
        onConfirm={handleModalConfirm}
        url={linkModal.url}
        isDarkMode={isDarkMode}
      />
    </>
  );
};
